'use client';

import { useEffect } from 'react';
import type { Locale } from '@/lib/content';

export interface WebMcpPlan {
  price: string;
  period?: string;
  orders: string;
  limits: string;
  description: string;
}

interface SearchDoc {
  type: string;
  title: string;
  description: string;
  url: string;
}

const REGISTER_URL = 'https://dash.winkelfactuur.nl/register';

async function searchSite(locale: Locale, query: string, limit: number) {
  const response = await fetch(`/${locale}/search-index.json`);
  const docs: SearchDoc[] = await response.json();
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);

  return docs
    .map((doc) => {
      const title = doc.title.toLowerCase();
      const description = doc.description.toLowerCase();
      const score = terms.reduce(
        (sum, term) => sum + (title.includes(term) ? 3 : 0) + (description.includes(term) ? 1 : 0),
        0
      );
      return { doc, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ doc }) => ({
      title: doc.title,
      description: doc.description,
      type: doc.type,
      url: new URL(doc.url, window.location.origin).href,
    }));
}

/**
 * Exposes read-only site capabilities to in-browser AI agents through WebMCP.
 * Registration runs on mount so the tools exist while the page is still loading.
 */
export function WebMcpTools({ locale, plans }: { locale: Locale; plans: WebMcpPlan[] }) {
  useEffect(() => {
    const modelContext = document.modelContext ?? navigator.modelContext;
    if (!modelContext?.registerTool) {
      return;
    }

    const controller = new AbortController();
    const tools: WebMcpTool[] = [
      {
        name: 'search_site',
        title: 'Search Winkel Factuur',
        description:
          'Search Winkel Factuur blog articles, guides, free tools and product pages about marketplace invoicing, VAT/OSS and bol.com, Amazon and Shopify selling. Returns up to `limit` results with title, description, type and absolute URL.',
        inputSchema: {
          type: 'object',
          properties: {
            query: { type: 'string', description: 'Search words, for example "bol.com btw factuur".' },
            limit: { type: 'integer', minimum: 1, maximum: 20, description: 'Maximum number of results. Defaults to 5.' },
          },
          required: ['query'],
        },
        annotations: { readOnlyHint: true },
        execute: async (input) =>
          searchSite(locale, String(input.query ?? ''), Math.min(Math.max(Number(input.limit) || 5, 1), 20)),
      },
      {
        name: 'get_pricing_plans',
        title: 'Winkel Factuur pricing',
        description:
          'List the Winkel Factuur subscription plans with monthly price (excluding VAT), invoice volume and store limits, plus the sign-up URL for the free 14-day trial.',
        inputSchema: { type: 'object', properties: {} },
        annotations: { readOnlyHint: true },
        execute: async () => ({ currency: 'EUR', vatIncluded: false, trialDays: 14, signupUrl: REGISTER_URL, plans }),
      },
    ];

    for (const tool of tools) {
      // Rejects when an agent host already holds a tool with this name; nothing to recover.
      Promise.resolve(modelContext.registerTool(tool, { signal: controller.signal })).catch(() => {});
    }

    return () => controller.abort();
  }, [locale, plans]);

  return null;
}
